import { useCallback, useEffect, useState } from "react";
import { Plus, FolderOpen, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import {
  Button, Input, Textarea, Label, Modal, ModalFooter, Badge, EmptyState,
  Skeleton, useToast, useConfirm,
} from "@/components/ui";
import { ApiError } from "@/lib/api";
import { storeApi, type StoreCollection, type StoreProduct } from "./api";

export function StoreCollectionsPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [collections, setCollections] = useState<StoreCollection[] | null>(null);
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [editing, setEditing] = useState<StoreCollection | "new" | null>(null);

  const load = useCallback(() => {
    storeApi.collections()
      .then((r) => setCollections(r.collections))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed to load collections"));
    storeApi.products().then((r) => setProducts(r.products)).catch(() => setProducts([]));
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const remove = async (c: StoreCollection) => {
    if (!(await confirm({ title: `Delete "${c.name}"?`, description: "Products stay — they just leave this collection.", confirmLabel: "Delete", danger: true }))) return;
    try { await storeApi.deleteCollection(c.id); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : "Failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Collections</h1>
          <p className="mt-1 text-sm text-zinc-400">Curated groups of products — shoppers browse and filter by these.</p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>New collection</Button>
      </div>

      {collections === null ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : collections.length === 0 ? (
        <EmptyState
          icon={FolderOpen}
          title="No collections yet"
          description="Group products into collections like “New arrivals” or “Best sellers”."
          action={<Button onClick={() => setEditing("new")}>New collection</Button>}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">Collection</th>
                <th className="px-4 py-2.5 font-medium">Products</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/70">
              {collections.map((c) => (
                <tr key={c.id} className="cursor-pointer bg-zinc-950 transition hover:bg-zinc-900/50" onClick={() => setEditing(c)}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-100">{c.name}</div>
                    <div className="text-xs text-zinc-500">/{c.slug}{c.description ? ` — ${c.description}` : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{c.product_count}</td>
                  <td className="px-4 py-3">
                    {c.published ? <Badge color="emerald" size="sm">Live</Badge> : <Badge color="zinc" size="sm">Hidden</Badge>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={(e) => { e.stopPropagation(); setEditing(c); }} className="rounded p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><Pencil className="h-4 w-4" /></button>
                      <button onClick={(e) => { e.stopPropagation(); void remove(c); }} className="rounded p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <CollectionEditor
          collection={editing === "new" ? null : editing}
          products={products}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function CollectionEditor({ collection, products, onClose, onSaved }: {
  collection: StoreCollection | null;
  products: StoreProduct[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: collection?.name ?? "",
    description: collection?.description ?? "",
    published: collection?.published ?? true,
    sort_order: collection?.sort_order ?? 0,
    product_ids: collection?.product_ids ?? [],
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const toggleProduct = (id: string) =>
    set("product_ids", form.product_ids.includes(id)
      ? form.product_ids.filter((x) => x !== id)
      : [...form.product_ids, id]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Please name the collection."); return; }
    setSaving(true);
    try {
      let id = collection?.id;
      const body = {
        name: form.name.trim(),
        description: form.description || null,
        published: form.published,
        sort_order: Number(form.sort_order) || 0,
      };
      if (id) {
        await storeApi.updateCollection(id, body);
      } else {
        const r = await storeApi.createCollection(body as never);
        id = r.id;
      }
      await storeApi.setCollectionProducts(id!, form.product_ids);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={collection ? "Edit collection" : "New collection"} size="lg">
      <div className="max-h-[70vh] space-y-4 overflow-y-auto p-5">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="c-name">Name</Label>
            <Input id="c-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="New arrivals" />
          </div>
          <div>
            <Label htmlFor="c-sort">Sort order</Label>
            <Input id="c-sort" type="number" value={String(form.sort_order)} onChange={(e) => set("sort_order", Number(e.target.value))} />
          </div>
        </div>
        <div>
          <Label htmlFor="c-desc">Description</Label>
          <Textarea id="c-desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="optional" />
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} className="accent-rose-500" />
          {form.published ? <Eye className="h-4 w-4 text-emerald-400" /> : <EyeOff className="h-4 w-4 text-zinc-500" />}
          Visible on the store
        </label>

        <div>
          <Label>Products in this collection ({form.product_ids.length})</Label>
          {products.length === 0 ? (
            <p className="text-sm text-zinc-500">No products yet — create some under Store → Products.</p>
          ) : (
            <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-zinc-800 p-2">
              {products.map((p) => (
                <label key={p.id} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-zinc-900">
                  <input
                    type="checkbox"
                    checked={form.product_ids.includes(p.id)}
                    onChange={() => toggleProduct(p.id)}
                    className="accent-rose-500"
                  />
                  <span className="min-w-0 flex-1 truncate text-zinc-200">{p.name}</span>
                  {!p.published && <Badge color="zinc" size="sm">Draft</Badge>}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving}>Save</Button>
      </ModalFooter>
    </Modal>
  );
}
